/**
 * TRT Biomarker Panel Configuration
 *
 * Defines standard biomarkers for TRT lab results verification.
 * Used by nurses to pre-populate biomarker entry form.
 */

export type BiomarkerCategory = 'hormones' | 'metabolic' | 'blood' | 'other'

export type BiomarkerStatus = 'normal' | 'low' | 'high' | 'critical' | undefined

export interface BiomarkerDefinition {
  code: string
  name: string
  unit: string
  referenceMin?: number
  referenceMax?: number
  category: BiomarkerCategory
}

export interface BiomarkerValue extends BiomarkerDefinition {
  value: number | null
  status?: BiomarkerStatus
  isCustom?: boolean
}

/**
 * Standard TRT biomarker panel
 * Reference ranges based on typical male adult values
 */
export const TRT_BIOMARKER_PANEL: BiomarkerDefinition[] = [
  // Hormones
  {
    code: 'TOTAL_T',
    name: 'Ukupni testosteron',
    unit: 'ng/dL',
    referenceMin: 300,
    referenceMax: 1000,
    category: 'hormones',
  },
  {
    code: 'FREE_T',
    name: 'Slobodni testosteron',
    unit: 'pg/mL',
    referenceMin: 5,
    referenceMax: 21,
    category: 'hormones',
  },
  {
    code: 'SHBG',
    name: 'SHBG',
    unit: 'nmol/L',
    referenceMin: 10,
    referenceMax: 57,
    category: 'hormones',
  },
  {
    code: 'LH',
    name: 'LH',
    unit: 'mIU/mL',
    referenceMin: 1.5,
    referenceMax: 9.3,
    category: 'hormones',
  },
  {
    code: 'FSH',
    name: 'FSH',
    unit: 'mIU/mL',
    referenceMin: 1.4,
    referenceMax: 18.1,
    category: 'hormones',
  },
  {
    code: 'ESTRADIOL',
    name: 'Estradiol (E2)',
    unit: 'pg/mL',
    referenceMin: 10,
    referenceMax: 40,
    category: 'hormones',
  },
  {
    code: 'PROLACTIN',
    name: 'Prolaktin',
    unit: 'ng/mL',
    referenceMin: 2,
    referenceMax: 18,
    category: 'hormones',
  },

  // Metabolic
  {
    code: 'PSA',
    name: 'PSA',
    unit: 'ng/mL',
    referenceMin: 0,
    referenceMax: 4,
    category: 'metabolic',
  },
  {
    code: 'GLUCOSE',
    name: 'Glukoza',
    unit: 'mg/dL',
    referenceMin: 70,
    referenceMax: 100,
    category: 'metabolic',
  },

  // Blood
  {
    code: 'HCT',
    name: 'Hematokrit',
    unit: '%',
    referenceMin: 38,
    referenceMax: 50,
    category: 'blood',
  },
  {
    code: 'HGB',
    name: 'Hemoglobin',
    unit: 'g/dL',
    referenceMin: 13.5,
    referenceMax: 17.5,
    category: 'blood',
  },
  {
    code: 'RBC',
    name: 'Eritrociti',
    unit: 'M/uL',
    referenceMin: 4.5,
    referenceMax: 5.5,
    category: 'blood',
  },
]

/**
 * Category labels in Serbian
 */
export const CATEGORY_LABELS: Record<BiomarkerCategory, string> = {
  hormones: 'Hormoni',
  metabolic: 'Metabolički',
  blood: 'Krvna slika',
  other: 'Ostalo',
}

/**
 * Category display order
 */
export const CATEGORY_ORDER: BiomarkerCategory[] = ['hormones', 'blood', 'metabolic', 'other']

/**
 * Calculate biomarker status based on value and reference range
 */
export function calculateBiomarkerStatus(
  value: number | null,
  referenceMin?: number,
  referenceMax?: number,
): BiomarkerStatus {
  if (value === null || value === undefined) {
    return undefined
  }

  if (referenceMin === undefined || referenceMax === undefined) {
    return undefined
  }

  // Critical if way out of range (50% below min or 100% above max)
  if (value < referenceMin * 0.5 || value > referenceMax * 2) {
    return 'critical'
  }

  if (value < referenceMin) {
    return 'low'
  }

  if (value > referenceMax) {
    return 'high'
  }

  return 'normal'
}

/**
 * Get status color for Ant Design components
 */
export function getStatusColor(status: BiomarkerStatus): string {
  switch (status) {
    case 'normal':
      return 'success'
    case 'low':
      return 'warning'
    case 'high':
      return 'warning'
    case 'critical':
      return 'error'
    default:
      return 'default'
  }
}

/**
 * Get status label in Serbian
 */
export function getStatusLabel(status: BiomarkerStatus): string {
  switch (status) {
    case 'normal':
      return 'Normalan'
    case 'low':
      return 'Nizak'
    case 'high':
      return 'Visok'
    case 'critical':
      return 'Kritičan'
    default:
      return '-'
  }
}

/**
 * Initialize biomarker values from TRT panel with existing data
 */
export function initializeBiomarkerValues(
  existingResults?: Array<{ code: string; value: number; unit?: string }>,
): BiomarkerValue[] {
  const existingMap = new Map(existingResults?.map((r) => [r.code, r.value]) || [])

  const values: BiomarkerValue[] = TRT_BIOMARKER_PANEL.map((def) => {
    const value = existingMap.get(def.code) ?? null
    return {
      ...def,
      value,
      status: calculateBiomarkerStatus(value, def.referenceMin, def.referenceMax),
      isCustom: false,
    }
  })

  // Add any custom markers from existing results that aren't in the panel
  const panelCodes = new Set(TRT_BIOMARKER_PANEL.map((d) => d.code))
  existingResults?.forEach((r) => {
    if (!panelCodes.has(r.code)) {
      values.push({
        code: r.code,
        name: r.code,
        unit: r.unit || '',
        value: r.value,
        category: 'other',
        isCustom: true,
      })
    }
  })

  return values
}

/**
 * Group biomarkers by category
 */
export function groupBiomarkersByCategory(
  values: BiomarkerValue[],
): Record<BiomarkerCategory, BiomarkerValue[]> {
  const grouped: Record<BiomarkerCategory, BiomarkerValue[]> = {
    hormones: [],
    metabolic: [],
    blood: [],
    other: [],
  }

  values.forEach((v) => {
    grouped[v.category].push(v)
  })

  return grouped
}

/**
 * Convert biomarker values to API format for saving
 */
export function biomarkersToApiFormat(
  values: BiomarkerValue[],
): Array<{ code: string; name: string; value: number; unit: string }> {
  return values
    .filter((v) => v.value !== null && v.value !== undefined)
    .map((v) => ({
      code: v.code,
      name: v.name,
      value: v.value as number,
      unit: v.unit,
    }))
}
